<template>
    <v-dialog scrollable v-model="dialog" transition="dialog-bottom-transition" persistent no-click-animation
        max-width="1500">
        <v-card class="card-container" min-height="650">
            <v-card-title class="title-container">
                <v-icon color="#FFFFFF" @click="hide" large class="mr-3">mdi-close-circle-outline</v-icon>
                {{ editMode ? $t('common.edit_circuit') : $t('common.create_circuit') }}
            </v-card-title>
            <v-card-text class="pt-3 px-3">
                <v-row v-if="!loadingEditMode" dense>
                    <!-- LOADING CATEGORIES -->
                    <v-col v-if="loadingCategories" cols="2" class="text-center">
                        <div class="container-full-center">
                            <loading-spinner />
                        </div>
                    </v-col>
                    <v-col v-else cols="1" class="text-center">
                        <div v-for="category in categoriesOptions" :key="category.id"
                            :class="['category-btn', { 'category-btn-selected': selectColorCategory(category) }, { 'black--text': !selectColorCategory(category) }]">
                            <button-tooltip @click="onClickCategory(category)"
                                :color="selectColorCategory(category) ? 'secondary' : 'primary'" large fab depressed
                                right :tooltip="category.name">
                                <v-icon x-large :color="selectColorCategory(category) ? '#FFFFFF' : '#000000'"
                                    v-text="category.icon" />
                            </button-tooltip>
                        </div>
                    </v-col>
                    <v-col v-if="!category" cols="11">
                        <v-alert color="secondary" outlined icon="mdi-alert-circle-outline" prominent border="left">
                            {{ $t('exercises.need_select_an_category') }}
                        </v-alert>
                    </v-col>
                    <v-col v-else cols="11">
                        <!-- "TABS TYPE" -->
                        <tabs-group :value="currentCircuitType" color="grey" :tabs="circuitTypes"
                            @change="currentCircuitType = $event" class="pb-2" />
                        <!-- "NAME" -->
                        <v-card color="grey lighten-4" flat>
                            <v-card-text>
                                <v-row align="center">
                                    <v-col cols="12">
                                        <kimsa-text-field :label="$t('circuits.name')" outlined dense force-value
                                            :solo="false" no-elevation :value="name" @change="name = $event" />
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                        <!-- "FILTERS" -->
                        <v-card class="mt-2" color="grey lighten-4" flat>
                            <v-card-text>
                                <v-row align="center">
                                    <!-- "TYPE EXERCISE" -->
                                    <v-col cols="5">
                                        <kimsa-combobox :label="$t('exercises.types')"
                                            :placeholder="$t('exercises.pick_type')" outlined dense
                                            :items="typesOptions" @change="types = $event" placeholder-class
                                            multiple-with-chips :readonly="!category" :value="types" ref="types" />
                                    </v-col>

                                    <!-- "EXERCISE" -->
                                    <v-col cols="5">
                                        <kimsa-combobox can-write @create="showCreateExercise"
                                            :label="$tc('exercises.exercises', 1)"
                                            :placeholder="$t('exercises.pick_exercise')" outlined dense
                                            :items="exercisesOptions" @change="onExerciseChange" item-value="objectID"
                                            placeholder-class ref="exercise" show-video :no-create-exercise="isFreePlan"
                                            @showVideo="onShowVideo" />
                                    </v-col>

                                    <!-- "QUANTITY SETS" -->
                                    <v-col cols="2">
                                        <kimsa-combobox :label="labelSets" :value="sets" outlined dense
                                            :items="setOptions" @change="sets = $event" placeholder-class ref="sets" />
                                    </v-col>
                                </v-row>
                                <v-row align="center" v-if="currentCircuitType === 'tabata'">
                                    <v-col cols="2">
                                        <kimsa-combobox :label="$t('common.laps')" :value="minutes" outlined
                                            dense :items="minutesOptions" @change="minutes = $event" placeholder-class
                                            ref="minutes" />
                                    </v-col>
                                    <v-col cols="2">
                                        <kimsa-combobox :label="$t('common.time_work')" :value="timeWork" outlined dense
                                            :items="secondOptions" @change="timeWork = $event" placeholder-class
                                            ref="timeWork" />
                                    </v-col>
                                    <v-col cols="2">
                                        <kimsa-combobox :label="$t('common.time_rest')" :value="timeRest" outlined dense
                                            :items="secondOptions" @change="timeRest = $event" placeholder-class
                                            ref="timeRest" />
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>

                        <!-- "EXERCISES" -->
                        <v-card class="mt-2" flat color="grey lighten-4">
                            <v-card-text>
                                <v-row align="center">
                                    <v-col cols="12">
                                        <small class="caption">{{ $tc('common.exercises', 2) }}</small>
                                        <v-divider class="my-2"></v-divider>
                                        <div class="text-center mt-4" v-if="exercises.length === 0">
                                            <small>{{ $t('common.add_exercises_for_config') }}</small>
                                        </div>
                                        <!-- "REPS/TIME" -->
                                        <v-row dense v-for="(exercise, i) in exercises" :key="exercise.objectID">
                                            <v-col cols="4">
                                                <v-text-field color="secondary" outlined dense hide-details readonly
                                                    v-model="exercise.name" />
                                            </v-col>
                                            <v-col cols="3" class="pb-0">
                                                <kimsa-combobox :placeholder="$t('common.select_placeholder')" outlined
                                                    dense height="40" :items="repsOptions"
                                                    @change="onChangeRepsType($event, i)" :value="exercise.config.type"
                                                    placeholder-class
                                                    :icon="exercise.config.type && icons[exercise.config.type]" />
                                            </v-col>
                                            <v-col cols="2">
                                                <v-text-field :label="$t('common.value')" min="0"
                                                    @keypress="repsValidation" color="secondary" outlined dense
                                                    hide-details :readonly="!exercise.config.type"
                                                    v-model="exercise.config.value" />
                                            </v-col>
                                            <v-col cols="2">
                                                <v-text-field :label="$t('common.note')" color="secondary" outlined
                                                    dense hide-details v-model="exercise.config.note" maxLength="6" />
                                            </v-col>
                                            <v-col cols="1" class="text-center">
                                                <v-btn fab x-small depressed dark class="mt-1"
                                                    @click="deleteExercise(i)" color="pink">
                                                    <v-icon>$delete</v-icon>
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <div v-else class="container-full-center">
                    <loading-spinner />
                </div>
            </v-card-text>
            <v-card-actions class="px-0 pb-0">
                <v-btn block depressed x-large color="primary" class="normal-btn no-radius black--text"
                    :disabled="disabledAdd" @click="add" :loading="loading">
                    {{ editMode ? $t('common.save') : $t('common.create') }}
                </v-btn>
            </v-card-actions>
            <exercise-creator-desktop-dialog is-second-dialog ref="creator" @success="onSuccessCreate" />
            <watch-video-dialog @hide="onHideVideo" :thumb="currentImg" :video="currentVideo" is-second-dialog
                ref="watchVideo" />
        </v-card>
    </v-dialog>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { Client, ExerciseIndex } from "@/utils/Algolia";
import { db, Timestamp } from "@/utils/Firebase";
import ExerciseCreatorDialog from "@/components/coach/routines/ExerciseCreatorDialog";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import ExerciseCreatorDesktopDialog from "@/components/coach/routines/ExerciseCreatorDesktopDialog";
import WatchVideoDialog from "@/components/common/routines/WatchVideoDialog";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import TabsGroup from '../../common/TabsGroup.vue';

export default {
    name: "CreateCircuitDialog",
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    components: {
        ButtonTooltip,
        LoadingSpinner,
        WatchVideoDialog,
        ExerciseCreatorDesktopDialog,
        KimsaTextField,
        KimsaCombobox,
        ExerciseCreatorDialog,
        TabsGroup
    },
    data(vm) {
        return {
            dialog: false,
            loading: false,
            editMode: false,
            loadingEditMode: false,
            circuit: null,
            circuitTypes: [
                { label: vm.$t('circuits.emom'), value: 'emom' },
                { label: vm.$t('circuits.amrap'), value: 'amrap' },
                { label: vm.$t('circuits.time'), value: 'time' },
                { label: vm.$t('circuits.tabata'), value: 'tabata' },
            ],
            name: '',
            currentCircuitType: 'emom',
            category: null,
            types: [],
            exercisesOptions: [],
            exercises: [],
            sets: 1,
            minutes: 1,
            timeWork: 1,
            timeRest: 1,
            icons: {
                reps: 'mdi-repeat',
                time: 'mdi-av-timer',
                minutes: 'mdi-clock-outline',
                distance: 'mdi-diameter-outline',
            },
            currentImg: null,
            currentVideo: null,
        }
    },
    computed: {
        ...mapState(['dialogActive', 'categoriesOptions', "loadingCategories", 'globalFolders']),
        labelSets(vm = this) {
            if (vm.currentCircuitType === 'emom' || vm.currentCircuitType === 'amrap') return vm.$tc('common.time')
            if (vm.currentCircuitType === 'time') return vm.$tc('common.laps')
            return vm.$tc('exercises.sets', 2)
        },
        setOptions(vm = this) {
            let quantity = 10
            if (vm.currentCircuitType === 'emom') quantity = 15
            if (vm.currentCircuitType === 'amrap') quantity = 25
            return Array.from({ length: quantity }, (_, i) => i + 1);
        },
        minutesOptions(vm = this) {
            let quantity = 10
            return Array.from({ length: quantity }, (_, i) => i + 1);
        },
        secondOptions(vm = this) {
            let quantity = 60
            return Array.from({ length: quantity }, (_, i) => i + 1);
        },
        repsOptions(vm = this) {
            if (!vm.hasIntensity) return [
                { name: vm.$t('exercises.reps'), value: 'reps' },
                { name: vm.$t('exercises.time'), value: 'time' },
                { name: vm.$t('exercises.distance'), value: 'distance' }
            ]
            return [
                { name: vm.$t('exercises.reps'), value: 'reps' },
                { name: vm.$t('exercises.minutes'), value: 'minutes' },
                { name: vm.$t('exercises.distance'), value: 'distance' }
            ]
        },
        typesOptions(vm = this) {
            if (!vm.category) return []
            return vm.category.types.map(type => ({ name: type, value: type }))
        },
        hasIntensity(vm = this) {
            return vm.category?.name === vm.$t('objectives.speed') || vm.category?.name === vm.$t('objectives.resistance')
        },
        disabledAdd(vm = this) {
            return !vm.name || !vm.category || !vm.sets || vm.exercises.length === 0 || vm.exercises.some(ex => !ex.config.type || !ex.config.value)
        },
        isCompany(vm = this) {
            return vm.accountType === 'company'
        },
        circuitFolder(vm = this) {
            return vm.globalFolders?.find(gf => gf.isCircuit) || null
        },
        circuitId(vm = this) {
            return vm.circuit?.id || vm.circuit?.objectID
        }
    },
    async mounted() {
        const vm = this
        // console.log(vm)
        if (vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
        if (vm.globalFolders.length === 0) await vm.bindGlobalFolders()
    },
    methods: {
        ...mapMutations(['setDialogActive']),
        ...mapActions(['fetchExerciseCategories', 'bindGlobalFolders']),
        show() {
            const vm = this
            vm.dialog = true
            vm.setDialogActive(true)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
            vm.reset()
        },
        reset() {
            const vm = this
            vm.editMode = false
            vm.name = ''
            vm.category = null
            vm.types = []
            vm.set = 1
            vm.exercises = []
        },

        async setEditingMode(circuit) {
            const vm = this
            vm.editMode = true
            vm.loadingEditMode = true
            vm.show()
            vm.circuit = circuit

            vm.currentCircuitType = vm.circuit.type
            vm.name = vm.circuit.name
            vm.exercises = vm.circuit.block.exercises
            vm.category = vm.categoriesOptions.find(category => category.name === vm.exercises[0].category)
            vm.types = []
            vm.sets = vm.circuit.sets || 1
            vm.minutes = vm.circuit.block.tabata?.minutes || 1
            vm.timeWork = vm.circuit.block.tabata?.timeWork || 1
            vm.timeRest = vm.circuit.block.tabata?.timeRest || 1

            vm.loadingEditMode = false
        },

        // CATEGORY
        onClickCategory(category) {
            const vm = this
            vm.category = category
        },
        selectColorCategory(category) {
            return this.category?.name === category.name
        },

        // EXERCISES
        async loadExercises() {
            let vm = this
            vm.exercisesOptions = []

            let filterCompany = (vm.account.accountType === 'coach_company' || vm.isCompany)
                ? `OR companyId: ${vm.account?.coachSubscription?.companyId || vm.currentUser.uid}`
                : '';
            let filter = `(coachId:"${vm.coachId}" ${filterCompany} OR owner:"app") AND hide!=1`

            if (vm.types?.length > 0) {
                let types = vm.types.map(type => type.name)
                filter += ` AND (types:"${types.join('" OR types:"')}")`
            }

            Client.clearCache()
            let { hits } = await ExerciseIndex.search('', { filters: filter, hitsPerPage: 10000 });
            vm.exercisesOptions = hits;
        },
        showCreateExercise(exercise) {
            const vm = this
            let payload = {
                name: exercise,
                types: vm.types?.map(type => type.name) || [],
                categories: [vm.category.name]
            }
            vm.$refs.creator.show(payload)
        },
        async onSuccessCreate(exercise) {
            const vm = this
            // console.log('exerciseCREATED', exercise)
            vm.exercisesOptions.unshift(exercise)
            vm.onExerciseChange(exercise)
        },
        async onShowVideo(item) {
            const vm = this
            vm.currentImg = item.img || item.video?.thumb || null
            vm.currentVideo = item.video?.url || null
            if (vm.$refs.exercise) vm.$refs.exercise.closeMenu()
            await vm.$refs.watchVideo.show()
        },
        onHideVideo() {
            if (this.$refs.exercise) this.$refs.exercise.openMenu()
        },
        onExerciseChange(exercise) {
            const vm = this;
            if (vm.exercises.find(ex => ex.objectID === exercise.objectID)) return;
            let payload = vm.generateExercise(exercise)
            vm.exercises.push(payload)
            if (vm.$refs.exercise) {
                vm.$nextTick(() => {
                    vm.$refs.exercise.model = null
                    vm.$refs.exercise.search = null
                })
            }
        },
        generateExercise(exercise) {
            return {
                id: exercise.objectID,
                objectID: exercise.objectID,
                type: 'exercise',
                name: exercise.name,
                config: exercise.config || { type: null, value: null, icon: null, note: null },
                category: exercise.categories[0] || exercise.category
            }
        },
        deleteExercise(index) {
            const vm = this;
            vm.exercises.splice(index, 1)
        },

        // SETS
        onChangeRepsType(repType, index) {
            const vm = this;
            let value = repType?.value || repType
            vm.exercises[index].config.type = value
            vm.exercises[index].config.icon = vm.icons[value]
        },
        // VALIDATIONS
        repsValidation(event) {
            const char = event.key
            const allowedChars = '0123456789()maxdropy -:'
            const isAllowed = allowedChars.includes(char.toLowerCase()) || char === 'Backspace'
            if (!isAllowed) event.preventDefault()
        },

        // ADD/EDIT
        async add() {
            const vm = this
            vm.loading = true

            let payload = {
                type: vm.currentCircuitType,
                name: vm.name,
                block: {
                    completed: false,
                    exercises: vm.exercises,
                    sets: vm.sets,
                    isCircuit: true,
                    type: vm.currentCircuitType,
                },
                sets: vm.sets,
                coachId: vm.coachId,
                folder: vm.circuitFolder?.id,
                folderName: vm.circuitFolder?.name,
                isCircuit: true,
            }

            if (vm.currentCircuitType === 'tabata') {
                payload.block.tabata = {
                    minutes: vm.minutes,
                    timeWork: vm.timeWork,
                    timeRest: vm.timeRest,
                    sets: vm.sets,
                }
            }

            console.log('payload', payload);
            if (vm.editMode) {
                await db.collection('blocks').doc(vm.circuitId).update(payload)
            } else {
                payload.created_at = Timestamp.now()
                await db.collection('blocks').add(payload)
            }

            vm.loading = false
            vm.$emit('success')
            vm.toast(vm.$t('circuits.saved'))
            vm.hide()
        },
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
        category() {
            this.types = []
        },
        types() {
            const vm = this
            vm.loadExercises()
        },
    }
}
</script>

<style scoped>
.set-input {
    width: 75px;
    display: inline-block;
    margin: 0 2px;
}

.category-btn {
    cursor: pointer;
    font-size: 12px;
    font-weight: bold;
    margin-bottom: 4px;
    border: 1px solid var(--v-secondary-base) !important;
    border-radius: 50%;
    display: inline-block;
}

.category-btn-selected {
    border: 1px solid var(--v-primary-base) !important;
}

.container-full-center {
    height: 100%;
    display: flex;
    align-items: center;
}

.card-container {
    border-radius: 8px;
    overflow: hidden;
}

.title-container {
    color: #FFFFFF;
    background: var(--v-backGroundPrimary-base);
}
</style>